import { useState, useEffect, useCallback } from 'react';
import { ethers } from 'ethers';
import { CONTRACTS, ABIS, ILK, ILK_WPLS, RPC_URLS } from '../utils/contracts';
import {
  WAD, RAY, RAD,
  oraclePlsPriceWad, jdaiTargetWad, matToWad, debtWadFromArt, wadToNumber
} from '../utils/vaultMath';

/**
 * System-wide stats readable WITHOUT a wallet, for the pre-connect landing page.
 * Uses the same resilient FallbackProvider pattern as usePulseXPrice — everything
 * here is a public view call over the RPC_URLS list; no signer, no account.
 */
export const usePublicStats = () => {
  const [stats, setStats] = useState({
    plsLocked: 0,          // PLS held by the vault engine (ETHJoin balance)
    jdaiSupply: 0,         // JDAI tokens in circulation
    totalDebt: 0,          // true system debt (Art * rate), JDAI
    debtCeiling: 0,        // ilk line, JDAI
    mintable: 0,           // ceiling - debt, JDAI
    goldTarget: 0,         // JDAI target price (par), USD — 1/1000 oz gold
    oraclePlsPrice: 0,     // system oracle PLS price, USD
    minCollateralPct: 150, // mat as a percentage
    minDebt: 0,            // dust floor, JDAI
    stabilityFee: null,    // % APY from the Jug, or null if unreadable
    isLoading: true,
    error: null
  });

  const fetchStats = useCallback(async () => {
    try {
      const provider = new ethers.FallbackProvider(
        RPC_URLS.map((url) => new ethers.JsonRpcProvider(url))
      );

      const vat = new ethers.Contract(CONTRACTS.VAT, ABIS.VAT, provider);
      const spotter = new ethers.Contract(CONTRACTS.SPOTTER, ABIS.SPOTTER, provider);
      const jdai = new ethers.Contract(CONTRACTS.JDAI, ABIS.JDAI, provider);
      const jug = new ethers.Contract(CONTRACTS.JUG, ABIS.JUG, provider);

      // Read BOTH collateral types: legacy PLS-A (native, now frozen) and WPLS-A
      // (Easy Vaults, where new borrowing happens). Capacity is the sum of both.
      const [plsA, wplsA, par, [, mat], supply, lockedPls, lockedWpls] =
        await Promise.all([
          vat.ilks(ILK),
          vat.ilks(ILK_WPLS),
          spotter.par(),
          spotter.ilks(ILK),
          jdai.totalSupply(),
          provider.getBalance(CONTRACTS.ETHJOIN),
          new ethers.Contract(CONTRACTS.PLS, ABIS.PLS, provider).balanceOf(CONTRACTS.GEMJOIN_WPLS_A)
        ]);

      // Stability fee is best-effort — a Jug hiccup must not blank the whole page.
      let stabilityFee = null;
      try {
        const [duty] = await jug.ilks(ILK_WPLS);
        const base = await jug.base();
        const perSecond = wadToNumber(((duty + base) * WAD) / RAY);
        stabilityFee = ((Math.pow(perSecond, 31536000) - 1) * 100).toFixed(2);
      } catch (e) { /* leave null; UI shows an em dash */ }

      // Combined across both live collateral types.
      const debt = wadToNumber(debtWadFromArt(plsA.Art, plsA.rate))
        + wadToNumber(debtWadFromArt(wplsA.Art, wplsA.rate));
      const ceiling = wadToNumber((plsA.line * WAD) / RAD) + wadToNumber((wplsA.line * WAD) / RAD);
      const spot = wplsA.spot > 0n ? wplsA.spot : plsA.spot; // same oracle; prefer the active ilk

      setStats({
        plsLocked: wadToNumber(lockedPls) + wadToNumber(lockedWpls), // native + wrapped
        jdaiSupply: wadToNumber(supply),
        totalDebt: debt,
        debtCeiling: ceiling,
        mintable: Math.max(0, ceiling - debt),
        goldTarget: wadToNumber(jdaiTargetWad(par)),
        oraclePlsPrice: wadToNumber(oraclePlsPriceWad(spot, mat, par)),
        minCollateralPct: wadToNumber(matToWad(mat)) * 100,
        minDebt: wadToNumber((wplsA.dust * WAD) / RAD),
        stabilityFee,
        isLoading: false,
        error: null
      });
    } catch (error) {
      console.error('Error fetching public stats:', error);
      setStats(prev => ({ ...prev, isLoading: false, error: error.message }));
    }
  }, []);

  useEffect(() => {
    fetchStats();
    const interval = setInterval(fetchStats, 60000);
    return () => clearInterval(interval);
  }, [fetchStats]);

  return { ...stats, refresh: fetchStats };
};
